import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';

export interface ChooseDialogData {
  title: string;
  message: string;
  option1Text: string;
  /** Optional: omit it for a single-button dialog (e.g. an "OK" report). */
  option2Text?: string;
  option1Color?: 'primary' | 'accent' | 'warn';
  option2Color?: 'primary' | 'accent' | 'warn';
  option1Value?: any;
  option2Value?: any;
  option1Disabled?: boolean;
  option2Disabled?: boolean;
  /** Optional third, neutral button (e.g. "Do not send") — closes with choice 'none'. */
  neutralOptionText?: string;
  /** Optional always-visible details block under the message (e.g. the recipients behind an option). */
  detailsToggleLabel?: string;
  detailsItems?: string[];
  /** When true, the dialog cannot be dismissed by clicking outside or pressing Escape. */
  disableClose?: boolean;
}

export interface ChooseDialogResult {
  choice: 'option1' | 'option2' | 'none' | null;
  value?: any;
}

@Component({
  selector: 'app-choose-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      <div mat-dialog-content class="dialog-content">
        <div [innerHTML]="data.message" class="message-text"></div>
        <div *ngIf="data.detailsItems?.length" class="details-block">
          <div *ngIf="data.detailsToggleLabel" class="details-label">{{ data.detailsToggleLabel }}</div>
          <ul class="details-list">
            <li *ngFor="let item of data.detailsItems">{{ item }}</li>
          </ul>
        </div>
      </div>
      <div mat-dialog-actions class="dialog-actions">
        <button
          *ngIf="data.neutralOptionText"
          mat-flat-button
          (click)="onNeutral()"
          class="action-btn neutral-btn">
          {{ data.neutralOptionText }}
        </button>
        <button
          *ngIf="data.option2Text"
          mat-flat-button
          [class]="getButtonClass(data.option2Color || 'warn')"
          [disabled]="data.option2Disabled"
          (click)="onChoice('option2')"
          class="action-btn cancel-btn">
          {{ data.option2Text }}
        </button>
        <button
          mat-flat-button
          [class]="getButtonClass(data.option1Color || 'primary')"
          [disabled]="data.option1Disabled"
          (click)="onChoice('option1')"
          class="action-btn confirm-btn"
          cdkFocusInitial>
          {{ data.option1Text }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 480px;
    }

    .dialog-title {
      font-size: 18px;
      font-weight: 600;
      color: #2a3547;
      margin: 0;
      padding: 24px 24px 16px 24px;
      line-height: 1.4;
    }

    .dialog-content {
      padding: 0 24px 24px 24px !important;
      color: #5a6a85;
    }

    .message-text {
      font-size: 14px;
      line-height: 1.6;
      color: #5a6a85;
    }

    /* Dark mode styles */
    :host-context(.dark-theme) .dialog-title,
    .dark-theme .dialog-title {
      color: #e0e0e0 !important;
    }

    :host-context(.dark-theme) .dialog-content,
    .dark-theme .dialog-content {
      color: #b0b8c4 !important;
    }

    :host-context(.dark-theme) .message-text,
    .dark-theme .message-text {
      color: #b0b8c4 !important;
    }

    /* Style pour mettre ATTENTION en rouge et en gras */
    .message-text ::ng-deep strong,
    .message-text ::ng-deep b {
      color: #fa896b;
      font-weight: 600;
      display: block;
      margin-top: 12px;
    }

    /* Si on utilise une classe .attention */
    .message-text ::ng-deep .attention {
      color: #fa896b;
      font-weight: 600;
      display: block;
      margin-top: 12px;
    }

    .dialog-actions {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: center;
      align-items: center;
      gap: 12px;
      padding: 0 24px 24px 24px !important;
      margin: 0 !important;
    }

    .action-btn {
      flex: 1;
      max-width: 250px;
      min-width: 0;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      text-transform: none;
      letter-spacing: 0.3px;
      transition: all 0.3s ease;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .neutral-btn {
      order: 0;
      background-color: #eef2f6 !important;
      color: #2a3547 !important;
    }

    .neutral-btn:hover {
      background-color: #dfe5ef !important;
      box-shadow: 0 4px 12px rgba(90, 106, 133, 0.2);
    }

    :host-context(.dark-theme) .neutral-btn,
    .dark-theme .neutral-btn {
      background-color: #3a4459 !important;
      color: #e0e0e0 !important;
    }

    :host-context(.dark-theme) .neutral-btn:hover,
    .dark-theme .neutral-btn:hover {
      background-color: #46536d !important;
    }

    .cancel-btn {
      order: 1;
    }

    .confirm-btn {
      order: 2;
    }

    .details-block {
      margin-top: 14px;
    }

    .details-label {
      font-size: 12px;
      font-weight: 600;
      color: #5a6a85;
      margin-bottom: 6px;
    }

    .details-list {
      margin: 0;
      padding: 4px 0;
      list-style: none;
      background: rgba(93, 135, 255, 0.06);
      border: 1px solid rgba(93, 135, 255, 0.15);
      border-radius: 8px;
      font-size: 13px;
      color: #2a3547;
      max-height: 180px;
      overflow-y: auto;
    }

    .details-list li {
      padding: 7px 14px;
    }

    .details-list li + li {
      border-top: 1px solid rgba(93, 135, 255, 0.12);
    }

    :host-context(.dark-theme) .details-label,
    .dark-theme .details-label {
      color: #b0b8c4;
    }

    :host-context(.dark-theme) .details-list,
    .dark-theme .details-list {
      background: rgba(93, 135, 255, 0.12);
      border-color: rgba(93, 135, 255, 0.25);
      color: #e0e0e0;
    }

    .bg-primary {
      background-color: #5d87ff !important;
      color: white !important;
    }

    .bg-primary:hover {
      background-color: #4570ea !important;
      box-shadow: 0 4px 12px rgba(93, 135, 255, 0.3);
    }

    .bg-accent {
      background-color: #49beff !important;
      color: white !important;
    }

    .bg-accent:hover {
      background-color: #39aef0 !important;
      box-shadow: 0 4px 12px rgba(73, 190, 255, 0.3);
    }

    .bg-warn {
      background-color: #ff6692 !important;
      color: white !important;
    }

    .bg-warn:hover {
      background-color: #ff4d7d !important;
      box-shadow: 0 4px 12px rgba(255, 102, 146, 0.3);
    }

    .action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
      background-color: #e0e0e0 !important;
      color: #9e9e9e !important;
    }

    .action-btn:disabled:hover {
      box-shadow: none !important;
    }

    /* Dark mode pour les boutons désactivés */
    :host-context(.dark-theme) .action-btn:disabled,
    .dark-theme .action-btn:disabled {
      background-color: #404040 !important;
      color: #6b7280 !important;
    }

    /* Responsive pour petits écrans */
    @media (max-width: 600px) {
      .dialog-container {
        min-width: 320px;
      }

      .dialog-actions {
        flex-direction: column;
        gap: 10px;
      }

      .action-btn {
        width: 100%;
        max-width: 100%;
      }

      .cancel-btn {
        order: 2;
      }

      .confirm-btn {
        order: 1;
      }
    }
  `]
})
export class ChooseDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ChooseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChooseDialogData
  ) { }

  onNeutral(): void {
    this.dialogRef.close({ choice: 'none' } as ChooseDialogResult);
  }

  onChoice(choice: 'option1' | 'option2'): void {
    // Empêcher le clic si l'option est désactivée
    if (choice === 'option1' && this.data.option1Disabled) {
      return;
    }
    if (choice === 'option2' && this.data.option2Disabled) {
      return;
    }

    const result: ChooseDialogResult = {
      choice: choice,
      value: choice === 'option1' ? this.data.option1Value : this.data.option2Value
    };
    this.dialogRef.close(result);
  }

  getButtonClass(color: 'primary' | 'accent' | 'warn'): string {
    switch (color) {
      case 'primary':
        return 'bg-primary';
      case 'accent':
        return 'bg-accent';
      case 'warn':
        return 'bg-warn';
      default:
        return 'bg-primary';
    }
  }
}

